import { first } from 'lodash';
import { common } from '@gosupersimple/types';

import { ensureUniqueFieldKey } from '@/core/operation';

import { PipelineEditorError } from '../edit-pipeline/errors';
import {
  Aggregation,
  AggregationExtendedType,
  AggregationType,
  Field,
  Fields,
  FilterCondition,
  FilteringAggregation,
  Metric,
  Property,
  aggregationZod,
} from '../types';
import { nameToKey } from '../edit-pipeline/utils';
import { createFieldOptionGrouped } from '../components/select-options/field';
import {
  isExprAggregation,
  isKeyedAggregation,
  isMetricAggregation,
  isSortedAggregation,
} from '../pipeline/operation';
import { NumericFieldTypes, isDateField } from '../model/utils';
import {
  FieldsOrGroups,
  filterFieldsBy,
  findField,
  findFieldFromGroups,
  firstField,
  firstFieldBy,
  flattenFieldGroups,
  getUniqueFieldName,
} from '../pipeline/utils';
import { metricById } from './metrics';

export interface AggregationOption {
  label: string;
  value: AggregationExtendedType;
  kind: 'key' | 'empty' | 'expr';
  fieldTypes?: string[];
}

export const AggregationOptions: AggregationOption[] = [
  { label: 'Count', value: 'count', kind: 'empty' },
  { label: 'Sum', value: 'sum', kind: 'key', fieldTypes: ['Number', 'Integer', 'Float'] },
  { label: 'Count Distinct', value: 'count_distinct', kind: 'key' },
  { label: 'Minimum', value: 'min', kind: 'key', fieldTypes: ['Number', 'Integer', 'Float'] },
  { label: 'Maximum', value: 'max', kind: 'key', fieldTypes: ['Number', 'Integer', 'Float'] },
  { label: 'Earliest', value: 'earliest', kind: 'key', fieldTypes: ['Date'] },
  { label: 'Latest', value: 'latest', kind: 'key', fieldTypes: ['Date'] },
  { label: 'First Value', value: 'first', kind: 'key' },
  { label: 'Average', value: 'avg', kind: 'key', fieldTypes: ['Number', 'Integer', 'Float'] },
  { label: 'Median', value: 'median', kind: 'key', fieldTypes: ['Number', 'Integer', 'Float'] },
  { label: 'List of', value: 'array', kind: 'key' },
];

const ChartExcludedTypes: AggregationExtendedType[] = ['earliest', 'latest', 'array'];

export const ChartAggregationOptions = AggregationOptions.filter(
  (option) => !ChartExcludedTypes.includes(option.value),
).map((option) =>
  option.value === 'first' ? { ...option, fieldTypes: NumericFieldTypes } : option,
);

const getAggregationOptionByType = (type: AggregationExtendedType) =>
  AggregationOptions.find((option) => option.value === type);

export const getAggregationOptionLabelByType = (type: AggregationExtendedType) =>
  getAggregationOptionByType(type)?.label;

const getFieldsForAggregationType = (
  type: AggregationExtendedType,
  fields: FieldsOrGroups,
  aggregationOptions = AggregationOptions,
) => {
  const aggregationOption = aggregationOptions.find((a) => a.value === type);

  return filterFieldsBy(fields, (field) => {
    return (
      field.type === null || (aggregationOption?.fieldTypes?.includes(field.type ?? '') ?? true)
    );
  });
};

export const getFieldsForAggregation = (aggregation: Aggregation, fields: Fields) =>
  getFieldsForAggregationType(
    aggregationTypeToAggregationExtendedType(
      aggregation.type,
      isKeyedAggregation(aggregation) ? fields.find((f) => f.key === aggregation.key) : undefined,
    ),
    fields,
  );

export const getFieldOptionsForAggregationType = (
  type: AggregationExtendedType,
  fields: FieldsOrGroups,
  aggregationOptions?: AggregationOption[],
) => getFieldsForAggregationType(type, fields, aggregationOptions).map(createFieldOptionGrouped);

export const getAggregationOptionsForFields = (
  fields: FieldsOrGroups,
  aggregationOptions = AggregationOptions,
) => {
  const flattenedFields = flattenFieldGroups(fields);
  return aggregationOptions.filter(({ fieldTypes }) =>
    flattenedFields.some(({ type }) => fieldTypes?.includes(type ?? '') ?? true),
  );
};

/**
 * Convert a normal aggregation type to an extended aggregation type for display.
 * The extended type includes types like `earlier` and `latest` that are converted back
 * into `min` and `max` for the backend.
 * Whether a type `min` should be `min` or `earlier` is determined by the type of the
 * field referenced by the aggregation.
 */
export const aggregationTypeToAggregationExtendedType = (
  aggregationType: AggregationType,
  field?: Field,
) =>
  AggregationOptions.find(
    (option) =>
      aggregationExtendedTypeToAggregationType(option.value) === aggregationType &&
      (option.fieldTypes === undefined || option.fieldTypes.includes(field?.type ?? '')),
  )?.value ?? aggregationType;

/**
 * Convert an extended aggregation type to a normal aggregation type for backend.
 */
export const aggregationExtendedTypeToAggregationType = (
  extendedType: AggregationExtendedType,
): AggregationType => {
  switch (extendedType) {
    case 'earliest':
      return 'min';
    case 'latest':
      return 'max';
    default:
      return extendedType;
  }
};

export const getAggregationLabel = (type: AggregationExtendedType, field?: Field) => {
  const fieldName = field?.name ?? 'Unknown';
  switch (type) {
    case 'count':
      return 'Count';
    case 'count_distinct':
      return `Count of ${fieldName} (distinct)`;
    case 'sum':
      return `Sum of ${fieldName}`;
    case 'min':
      return `Min ${fieldName}`;
    case 'max':
      return `Max ${fieldName}`;
    case 'earliest':
      return `Earliest ${fieldName}`;
    case 'latest':
      return `Latest ${fieldName}`;
    case 'avg':
      return `Average ${fieldName}`;
    case 'median':
      return `Median ${fieldName}`;
    case 'first':
      return `First Value of ${fieldName}`;
    case 'last':
      return `Last Value of ${fieldName}`;
    case 'array':
      return `List of ${fieldName}`;
    case 'custom':
      return 'Custom Formula';
    default:
      return 'Unknown Value';
  }
};

const generateMetricAggregationProperty = (metric: Metric, fields: Field[]) => {
  return {
    key: ensureUniqueFieldKey(fields, metric.metricId),
    name: getUniqueFieldName(fields, metric.name),
  };
};

const generateKeyedAggregationProperty = (
  aggregationType: AggregationType,
  field: Field,
  fieldNameSuffix: string | undefined,
  existingFields: Field[],
) => {
  return {
    key: ensureUniqueFieldKey(existingFields, `${aggregationType}_${field.key}`),
    name: getUniqueFieldName(
      existingFields,
      `${getAggregationLabel(aggregationType, field)}${
        fieldNameSuffix !== undefined ? ` (${fieldNameSuffix})` : ''
      }`,
    ),
  };
};

const generateExprAggregationProperty = (fields: Field[]) => {
  return {
    key: ensureUniqueFieldKey(fields, 'custom'),
    name: getUniqueFieldName(fields, 'Custom Formula'),
  };
};

const generateCountAggregationProperty = (
  relatedFieldSuffix: string | undefined,
  fields: Field[],
) => {
  if (relatedFieldSuffix !== undefined) {
    return {
      key: ensureUniqueFieldKey(fields, `count_${nameToKey(relatedFieldSuffix)}`),
      name: getUniqueFieldName(fields, `Number of ${relatedFieldSuffix}`),
    };
  }

  return {
    key: ensureUniqueFieldKey(fields, 'count'),
    name: getUniqueFieldName(fields, 'Count'),
  };
};

export const generateAggregationProperty = (
  aggregation: Aggregation,
  existingFields: Field[],
  aggregationFields: FieldsOrGroups,
  metrics: Metric[],
  fieldNameSuffix?: string,
): Property => {
  if (isKeyedAggregation(aggregation)) {
    const field = findField(aggregationFields, aggregation.key);
    if (field === undefined) {
      throw new PipelineEditorError(`Aggregation references unknown field '${aggregation.key}'`);
    }
    return generateKeyedAggregationProperty(
      aggregation.type,
      field,
      fieldNameSuffix,
      existingFields,
    );
  }

  if (isMetricAggregation(aggregation)) {
    const metric = metrics.find((m) => m.metricId === aggregation.metricId);
    if (metric === undefined) {
      throw new PipelineEditorError(
        `Aggregation references unknown metric ${aggregation.metricId}`,
      );
    }
    return generateMetricAggregationProperty(metric, existingFields);
  }

  if (isExprAggregation(aggregation)) {
    return generateExprAggregationProperty(existingFields);
  }

  return generateCountAggregationProperty(fieldNameSuffix, existingFields);
};

const getNextAggregationType = (
  fields: FieldsOrGroups,
  allowedAggregationOptions = AggregationOptions,
): AggregationExtendedType => {
  const aggregationOptions = getAggregationOptionsForFields(fields, allowedAggregationOptions);
  if (fields.length === 0) {
    throw new PipelineEditorError('No fields available for aggregation');
  }

  const aggregationOption =
    aggregationOptions.find(
      ({ fieldTypes }) =>
        firstFieldBy(
          fields,
          (field) =>
            fieldTypes === undefined ||
            (field.type !== 'Date' && fieldTypes.includes(field.type ?? '')),
        ) !== undefined,
    ) ?? first(aggregationOptions);

  return aggregationOption?.value ?? 'count';
};

export const getDefaultAggregationFromOptionValue = (
  value: string,
  context: { existingFields: Field[]; fields: FieldsOrGroups; metrics: Metric[] },
) => {
  const aggregationOption = AggregationOptions.find((option) => option.value === value);
  if (aggregationOption !== undefined) {
    return getDefaultAggregation(
      context.existingFields,
      context.fields,
      context.metrics,
      undefined,
      [aggregationOption],
    );
  }

  const metric = metricById(context.metrics, value);
  if (metric !== undefined) {
    return {
      type: 'metric',
      property: generateMetricAggregationProperty(metric, context.existingFields),
      metricId: metric.metricId,
    } satisfies common.Aggregation;
  }

  throw new Error(`Could not find default aggregation for value ${value}`);
};

const buildRelation = (field: Field): common.AggregationRelation | undefined => {
  if (
    field.relation !== undefined &&
    field.model !== undefined &&
    field.model.modelId !== field.relation.baseModelId
  ) {
    return {
      key: field.relation.key,
      modelId: field.relation.baseModelId,
    };
  }
  return undefined;
};

export const getDefaultAggregation = (
  existingFields: Fields,
  aggregationFields: FieldsOrGroups,
  metrics: Metric[],
  fieldNameSuffix?: string,
  allowedAggregationOptions = AggregationOptions,
): Aggregation => {
  const type = getNextAggregationType(aggregationFields, allowedAggregationOptions);

  if (type === 'count') {
    return {
      type: 'count',
      property: generateCountAggregationProperty(fieldNameSuffix, existingFields),
    };
  }

  if (type === 'metric') {
    const firstMetric = first(metrics);
    if (firstMetric === undefined) {
      throw new PipelineEditorError('No metrics available for aggregation');
    }
    return {
      type: 'metric',
      metricId: firstMetric.metricId,
      property: generateMetricAggregationProperty(firstMetric, existingFields),
    };
  }

  if (type === 'custom') {
    return {
      type: 'custom',
      value: { expression: '', version: '1' },
      property: generateExprAggregationProperty(existingFields),
    };
  }

  if (type === 'earliest' || type === 'latest') {
    const field = firstFieldBy(aggregationFields, isDateField) ?? firstField(aggregationFields);
    if (field === undefined) {
      throw new PipelineEditorError('No date fields available for aggregation');
    }

    const convertedType = type === 'earliest' ? 'min' : 'max';

    return {
      type: convertedType,
      key: field.key,
      property: generateKeyedAggregationProperty(
        convertedType,
        field,
        fieldNameSuffix,
        existingFields,
      ),
      relation: buildRelation(field),
    };
  }

  if (type === 'first' || type === 'last') {
    const field = firstField(
      getFieldsForAggregationType(type, aggregationFields, allowedAggregationOptions),
    );
    if (field === undefined) {
      throw new PipelineEditorError('No fields available for aggregation');
    }

    return {
      type,
      key: field.key,
      sort: [{ key: field.key, direction: 'ASC' }],
      property: generateKeyedAggregationProperty(type, field, fieldNameSuffix, existingFields),
      relation: buildRelation(field),
    };
  }

  const field = firstField(getFieldsForAggregationType(type, aggregationFields));
  if (field === undefined) {
    throw new PipelineEditorError('No fields available for aggregation');
  }

  return {
    type,
    key: field.key,
    property: generateKeyedAggregationProperty(type, field, fieldNameSuffix, existingFields),
    relation: buildRelation(field),
  };
};

/**
 * Validate (and where possible repair) an aggregation. Returns the valid
 * aggregation, or `undefined` when it is invalid and must be reset to a default.
 */
const validateAggregation = (
  aggregation: Aggregation,
  existingFields: Field[],
  aggregationFields: Field[],
  metrics: Metric[],
): Aggregation | undefined => {
  const validation = aggregationZod.safeParse(aggregation);

  if (!validation.success) {
    return undefined;
  }

  aggregation = validation.data;

  if (aggregation.type === 'count') {
    return {
      type: 'count',
      property: aggregation.property,
    };
  }

  if (aggregation.type === 'custom') {
    return {
      type: 'custom',
      value: aggregation.value,
      property: aggregation.property,
    };
  }

  if (aggregation.type === 'metric') {
    const metric = metrics.find((metric) => metric.metricId === aggregation.metricId);
    return metric ? aggregation : undefined;
  }

  const defaultTargetField = firstField(getFieldsForAggregation(aggregation, aggregationFields));
  if (defaultTargetField === undefined) {
    return undefined;
  }

  // When the referenced field is missing we swap in a fallback field. The property key/name still
  // describe the old field, so regenerate them to match the replacement.
  const replaceField = (field: Field): Aggregation => ({
    ...aggregation,
    key: field.key,
    property: generateKeyedAggregationProperty(aggregation.type, field, undefined, existingFields),
  });

  if (aggregation.key === undefined) {
    return replaceField(defaultTargetField);
  }

  const field = aggregationFields.find((f) => f.key === aggregation.key);
  if (!field) {
    return replaceField(defaultTargetField);
  }

  return aggregation;
};

export const ensureValidAggregation = (
  aggregation: Aggregation,
  existingFields: Field[],
  aggregationFields: Field[],
  metrics: Metric[],
  relationName?: string,
): Aggregation =>
  validateAggregation(aggregation, existingFields, aggregationFields, metrics) ??
  getDefaultAggregation(existingFields, aggregationFields, metrics, relationName);

export const ensureValidAggregations = (
  aggregations: Aggregation[],
  existingFields: Field[],
  aggregationFields: Field[],
  metrics: Metric[],
): Aggregation[] =>
  aggregations.reduce<Aggregation[]>((validAggregations, aggregation) => {
    const existingFieldsWithSiblings = [
      ...existingFields,
      ...validAggregations.map((agg) => ({
        key: agg.property.key,
        name: agg.property.name,
        type: null,
      })),
    ];

    const validated = validateAggregation(
      aggregation,
      existingFieldsWithSiblings,
      aggregationFields,
      metrics,
    );
    if (validated !== undefined) {
      return [...validAggregations, validated];
    }

    const defaultAggregation = getDefaultAggregation(
      existingFieldsWithSiblings,
      aggregationFields,
      metrics,
    );

    // Resetting an invalid aggregation always produces the same default type, so
    // skip it when an aggregation of that type is already present to avoid
    // generating duplicates with conflicting property keys/names.
    if (validAggregations.some((agg) => agg.type === defaultAggregation.type)) {
      return validAggregations;
    }

    return [...validAggregations, defaultAggregation];
  }, []);

export const hasDefaultPropertyName = (
  aggregation: Aggregation,
  existingFields: Field[],
  aggregationFields: FieldsOrGroups,
  metrics: Metric[],
  fieldNameSuffix?: string,
) => {
  const defaultProperty = generateAggregationProperty(
    aggregation,
    existingFields,
    aggregationFields,
    metrics,
    fieldNameSuffix,
  );

  return aggregation.property.name === defaultProperty.name;
};

/**
 * Naming context for aggregation edits. Lets `changeAggregationType` and
 * `changeAggregationKey` keep an auto-generated column name in sync with the
 * edited aggregation ("Count" → "Sum of Amount") while leaving user-typed
 * names untouched.
 */
interface AggregationNamingContext {
  /** Fields the aggregation's property name/key must stay unique against */
  existingFields: Field[];
  /** Fields available as aggregation targets */
  aggregationFields: FieldsOrGroups;
  metrics: Metric[];
  aggregationOptions?: AggregationOption[];
  fieldNameSuffix?: string;
}

/**
 * Whether the aggregation still carries its default, auto-generated name.
 * When a referenced entity is missing (e.g. a metric that has been deleted)
 * the default name cannot be computed — treat the name as custom so it is
 * preserved rather than blowing up a cosmetic concern.
 */
const canRegeneratePropertyName = (
  aggregation: Aggregation,
  { existingFields, aggregationFields, metrics, fieldNameSuffix }: AggregationNamingContext,
) => {
  try {
    return hasDefaultPropertyName(
      aggregation,
      existingFields,
      aggregationFields,
      metrics,
      fieldNameSuffix,
    );
  } catch {
    return false;
  }
};

/** Regenerate the default property if the previous name was a default (SUP-5448) */
const withRegeneratedProperty = (
  previous: Aggregation,
  changed: Aggregation,
  context: AggregationNamingContext,
): Aggregation => {
  if (changed === previous || !canRegeneratePropertyName(previous, context)) {
    return changed;
  }

  return {
    ...changed,
    property: generateAggregationProperty(
      changed,
      context.existingFields,
      context.aggregationFields,
      context.metrics,
      context.fieldNameSuffix,
    ),
  };
};

/**
 * Change an aggregation's type, keeping its column name sensible: a default
 * name is regenerated to match the new type ("Count" → "Sum of Amount"), a
 * custom name is preserved.
 *
 * All editors (sidebar pipeline editor, inline chart header form, ...) must go
 * through this shared entry point so their naming behaviour cannot diverge.
 */
export const changeAggregationType = (
  aggregation: Aggregation,
  newExtendedType: AggregationExtendedType,
  context: AggregationNamingContext,
): Aggregation =>
  withRegeneratedProperty(
    aggregation,
    convertAggregationToType(
      aggregation,
      newExtendedType,
      context.aggregationFields,
      context.aggregationOptions,
    ),
    context,
  );

/**
 * Convert an aggregation to a new type, preserving the property untouched.
 * Naming is handled by `changeAggregationType` — this stays internal so no
 * caller can accidentally skip the naming step.
 */
const convertAggregationToType = (
  aggregation: Aggregation,
  newExtendedType: AggregationExtendedType,
  aggregationFields: FieldsOrGroups,
  aggregationOptions = AggregationOptions,
): Aggregation => {
  const newType = aggregationExtendedTypeToAggregationType(newExtendedType);
  if (newType === 'metric') {
    return aggregation;
  }

  if (newType === 'custom') {
    return {
      type: newType,
      value: { expression: '', version: '1' },
      property: aggregation.property,
    };
  }

  if (newType === 'count') {
    return { type: newType, property: aggregation.property };
  }

  const validFields = getFieldsForAggregationType(
    newExtendedType,
    aggregationFields,
    aggregationOptions,
  );
  const prevField = isKeyedAggregation(aggregation)
    ? findField(aggregationFields, aggregation.key)
    : undefined;

  const allowedTypes = aggregationOptions.find((o) => o.value === newExtendedType)?.fieldTypes;
  const strictValidFields =
    allowedTypes !== undefined
      ? filterFieldsBy(validFields, (f) => allowedTypes.includes(f.type ?? ''))
      : validFields;

  const field =
    findField(validFields, prevField?.key ?? '') ??
    firstField(strictValidFields) ??
    firstField(validFields);
  if (field === undefined) {
    return aggregation;
  }

  if (newType === 'first' || newType === 'last') {
    const defaultSortField =
      firstFieldBy(aggregationFields, isDateField) ?? firstField(aggregationFields);

    const sort = isSortedAggregation(aggregation)
      ? aggregation.sort
      : [{ key: defaultSortField?.key ?? '', direction: 'ASC' as const }];

    return {
      type: newType,
      key: field.key,
      property: aggregation.property,
      sort,
      relation: buildRelation(field),
    };
  }

  return {
    type: newType,
    key: field.key,
    property: aggregation.property,
    relation: buildRelation(field),
  };
};

/**
 * Change a keyed aggregation's target field, keeping its column name sensible:
 * a default name is regenerated to match the new field ("Sum of Amount" →
 * "Sum of Price"), a custom name is preserved.
 *
 * Like `changeAggregationType`, this is the single entry point all editors
 * must share.
 */
export const changeAggregationKey = (
  aggregation: Aggregation,
  key: string,
  relation: { key: string; modelId?: string } | undefined,
  context: AggregationNamingContext,
): Aggregation => {
  const field = findFieldFromGroups(context.aggregationFields, { key, relation });
  if (field === undefined) {
    return aggregation;
  }

  if (!isKeyedAggregation(aggregation)) {
    throw new Error(`Cannot change field of aggregation type ${aggregation.type}`);
  }

  return withRegeneratedProperty(
    aggregation,
    { ...aggregation, key, relation: buildRelation(field) },
    context,
  );
};

export const buildMetricAggregation = (metric: Metric): Aggregation => ({
  type: 'metric',
  property: { key: nameToKey(metric.name), name: metric.name },
  metricId: metric.metricId,
});

export const buildDefaultAggregationFilter = (
  aggregation: FilteringAggregation,
  fields: FieldsOrGroups,
): FilterCondition => ({
  key: 'key' in aggregation ? aggregation.key : (flattenFieldGroups(fields).at(0)?.key ?? ''),
  operator: '==',
  value: '',
});
